// ============================================================
// TopWinnersAgent.js — InvestorQ Top Winners UI v3
// Option C: anchor + dynamic pool, history-aware, discoveries tab
// ============================================================
import React, { useState, useRef, useEffect } from 'react';
import HowItWorksModal, { HowItWorksButton } from './HowItWorksModal';
import {
  Trophy, TrendingUp, TrendingDown, Zap, RefreshCw, Loader,
  PieChart, Target, Briefcase, Flame,
  Shield, AlertTriangle, BarChart3, Brain, ArrowUpRight,
  ChevronDown, ChevronUp, Clock, Star, Layers, Compass,
  Database, Activity
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'https://financial-analyst-backend-production-7175.up.railway.app/api';

// ── Helpers ──────────────────────────────────────────────────
const SCORE_COLOR = s => s >= 85 ? '#10b981' : s >= 70 ? '#f59e0b' : '#ef4444';
const RANK_ACCENT = ['#ffd700','#c0c0c0','#cd7f32','#60a5fa','#60a5fa','#a78bfa','#a78bfa','#f472b6','#f472b6','#94a3b8'];

// ── Journey map — updated for Option C ───────────────────────
const JOURNEY_STEPS = [
  { id: 'pool',     icon: '🎯', label: 'Build pool',       sub: '50 anchor + 50 dynamic stocks',         color: '#3b82f6' },
  { id: 'av',       icon: '📡', label: 'Fetch signals',    sub: 'OVERVIEW · EARNINGS · QUOTE · RSI',      color: '#8b5cf6' },
  { id: 'alpha',    icon: '⚡', label: 'Alpha Intel™',     sub: 'Sentiment · shift · impact · flags',     color: '#f59e0b' },
  { id: 'earnings', icon: '📊', label: 'Earnings quality', sub: 'Beat rate · EPS growth · surprise%',     color: '#10b981' },
  { id: 'history',  icon: '🧠', label: 'Load history',     sub: 'Prior runs · rank momentum · patterns',  color: '#06b6d4' },
  { id: 'claude',   icon: '✨', label: 'Claude synthesis', sub: 'Multi-signal + history-aware ranking',   color: '#a78bfa' },
  { id: 'output',   icon: '🏆', label: 'Ranked results',   sub: 'Top 10 · sectors · discoveries',        color: '#ffd700' },
];

// ── Journey Map ───────────────────────────────────────────────
const JourneyMap = ({ activeStep }) => (
  <div style={{
    background: 'rgba(0,0,0,0.3)', border: '1px solid #1e293b',
    borderRadius: '14px', padding: '1rem 1.25rem', marginBottom: '1.25rem',
  }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.85rem' }}>
      <Layers size={13} color="#818cf8" />
      <span style={{ color: '#818cf8', fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
        Signal pipeline — option C
      </span>
    </div>
    <div style={{ display: 'flex', alignItems: 'center', gap: 0, overflowX: 'auto', paddingBottom: '0.25rem' }}>
      {JOURNEY_STEPS.map((step, i) => {
        const isActive = activeStep === step.id;
        const isDone   = activeStep && JOURNEY_STEPS.findIndex(s => s.id === activeStep) > i;
        const isLast   = i === JOURNEY_STEPS.length - 1;
        return (
          <React.Fragment key={step.id}>
            <div style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center',
              minWidth: 72, padding: '0.5rem 0.4rem',
              background: isActive ? `${step.color}18` : isDone ? 'rgba(16,185,129,0.06)' : 'transparent',
              borderRadius: '10px',
              border: isActive ? `1px solid ${step.color}44` : '1px solid transparent',
              transition: 'all 0.3s',
            }}>
              <div style={{
                width: 36, height: 36, borderRadius: '50%', fontSize: '1rem',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: isDone ? 'rgba(16,185,129,0.15)' : isActive ? `${step.color}22` : 'rgba(255,255,255,0.04)',
                border: `2px solid ${isDone ? '#10b981' : isActive ? step.color : '#1e293b'}`,
                marginBottom: '0.35rem', flexShrink: 0,
                boxShadow: isActive ? `0 0 12px ${step.color}55` : 'none',
                transition: 'all 0.3s',
              }}>
                {isDone ? '✓' : step.icon}
              </div>
              <span style={{
                fontSize: '0.6rem', fontWeight: 700, textAlign: 'center', lineHeight: 1.3,
                color: isActive ? step.color : isDone ? '#34d399' : '#475569',
                whiteSpace: 'nowrap',
              }}>{step.label}</span>
              {isActive && (
                <span style={{
                  fontSize: '0.55rem', color: '#64748b', textAlign: 'center',
                  lineHeight: 1.3, marginTop: '0.15rem',
                  maxWidth: 68, whiteSpace: 'normal',
                }}>{step.sub}</span>
              )}
            </div>
            {!isLast && (
              <div style={{
                width: 20, height: 2, flexShrink: 0, borderRadius: 1,
                background: isDone ? '#10b981' : '#1e293b',
                transition: 'background 0.3s',
              }} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  </div>
);

// ── Score ring ────────────────────────────────────────────────
const ScoreRing = ({ score }) => {
  const r = 22, c = 2 * Math.PI * r, dash = (score / 100) * c;
  const color = SCORE_COLOR(score);
  return (
    <svg width={54} height={54} style={{ flexShrink: 0, transform: 'rotate(-90deg)' }}>
      <circle cx={27} cy={27} r={r} fill="none" stroke="rgba(255,255,255,0.07)" strokeWidth="5" />
      <circle cx={27} cy={27} r={r} fill="none" stroke={color} strokeWidth="5"
        strokeDasharray={`${dash} ${c}`} strokeLinecap="round"
        style={{ filter: `drop-shadow(0 0 4px ${color}88)` }} />
      <text x="50%" y="50%" textAnchor="middle" dominantBaseline="middle"
        fill={color} fontSize="11" fontWeight="700" fontFamily="monospace"
        style={{ transform: 'rotate(90deg)', transformOrigin: 'center' }}>
        {score}
      </text>
    </svg>
  );
};

// ── Sparkline (synthetic — replaced with real when history exists) ────
const Sparkline = ({ score, trend }) => {
  const pts = React.useMemo(() => {
    const seed = score * 7 + (trend === 'breakout' ? 100 : trend === 'up' ? 50 : 0);
    const vals = Array.from({ length: 14 }, (_, i) => {
      const base = 30 + ((seed + i * 17) % 40);
      return trend === 'up'       ? base + i * 2.5 + ((seed * i) % 8)
           : trend === 'breakout' ? (i < 8 ? base + ((seed * i) % 10) : base + (i - 7) * 5.5)
           : base + ((seed * i) % 12) - 6;
    });
    const min = Math.min(...vals), max = Math.max(...vals), range = max - min || 1;
    return vals.map((v, i) => `${(i / 13) * 80},${28 - ((v - min) / range) * 28}`).join(' ');
  }, [score, trend]);
  return (
    <svg width={80} height={30} viewBox="0 0 80 30" style={{ display: 'block' }}>
      <polyline points={pts} fill="none" stroke={SCORE_COLOR(score)}
        strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
};

// ── Signal pill ───────────────────────────────────────────────
const SIGNAL_KEYS   = ['earnings','cashflow','alpha','momentum','sentiment','institutional'];
const SIGNAL_LABELS = ['Earnings','Cash Flow','Alpha Intel','Momentum','Sentiment','Institutional'];

const Pill = ({ label, value, positive }) => (
  <span style={{
    display: 'inline-flex', alignItems: 'center', gap: '0.2rem',
    padding: '0.2rem 0.55rem', borderRadius: '999px', fontSize: '0.68rem', fontWeight: 600,
    background: positive ? 'rgba(16,185,129,0.12)' : 'rgba(239,68,68,0.12)',
    color: positive ? '#34d399' : '#f87171',
    border: `1px solid ${positive ? 'rgba(16,185,129,0.25)' : 'rgba(239,68,68,0.25)'}`,
    whiteSpace: 'nowrap',
  }}>
    {positive ? <ArrowUpRight size={9}/> : <TrendingDown size={9}/>}
    {label}: {value}
  </span>
);

// ── Dynamic discovery badge ───────────────────────────────────
const DiscoveryBadge = () => (
  <span style={{
    display: 'inline-flex', alignItems: 'center', gap: '0.2rem',
    padding: '0.15rem 0.5rem', borderRadius: '999px', fontSize: '0.65rem', fontWeight: 700,
    background: 'rgba(6,182,212,0.15)', color: '#22d3ee',
    border: '1px solid rgba(6,182,212,0.3)',
  }}>
    <Compass size={9}/> Discovery
  </span>
);

// ── History badge ─────────────────────────────────────────────
const HistoryBadge = ({ runs }) => (
  <span style={{
    display: 'inline-flex', alignItems: 'center', gap: '0.2rem',
    padding: '0.15rem 0.5rem', borderRadius: '999px', fontSize: '0.65rem', fontWeight: 700,
    background: 'rgba(99,102,241,0.15)', color: '#818cf8',
    border: '1px solid rgba(99,102,241,0.3)',
  }}>
    <Database size={9}/> {runs} runs
  </span>
);

// ── Stock card ────────────────────────────────────────────────
const StockCard = ({ p, i, expanded, setExpanded, onStockClick, showRank = true }) => {
  const isOpen  = expanded === p.symbol;
  const accent  = RANK_ACCENT[Math.min(i, 9)];
  const signals = p.signals || {};

  return (
    <div style={{
      background: isOpen ? `${accent}08` : 'rgba(15,23,42,0.6)',
      border: `1px solid ${isOpen ? accent + '44' : p.isDynamic ? 'rgba(6,182,212,0.2)' : '#1e293b'}`,
      borderRadius: '14px', overflow: 'hidden',
      transition: 'border-color 0.2s',
      boxShadow: isOpen ? `0 0 18px ${accent}18` : 'none',
    }}>
      {/* Card header row */}
      <div onClick={() => setExpanded(isOpen ? null : p.symbol)} style={{
        display: 'grid',
        gridTemplateColumns: showRank ? '44px 1fr auto auto' : '1fr auto auto',
        alignItems: 'center', gap: '0.5rem',
        padding: '0.75rem 0.75rem', cursor: 'pointer',
      }}>
        {showRank && (
          <div style={{
            width: 44, height: 44, borderRadius: '10px',
            background: `${accent}12`, border: `2px solid ${accent}44`,
            display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <span style={{ fontSize: '0.62rem', color: accent, fontWeight: 700, lineHeight: 1 }}>#{p.rank}</span>
            {p.rank === 1 && <Trophy size={14} color={accent} style={{ marginTop: 2 }}/>}
            {p.rank === 2 && <Star    size={13} color={accent} style={{ marginTop: 2 }}/>}
            {p.rank >= 3  && <TrendingUp size={12} color={accent} style={{ marginTop: 2 }}/>}
          </div>
        )}

        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.45rem', flexWrap: 'wrap' }}>
            {/* Ticker */}
            <span style={{
              fontSize: '1.05rem', fontWeight: 800, color: '#f1f5f9', letterSpacing: '-0.01em',
              cursor: onStockClick ? 'pointer' : 'default',
              textDecoration: onStockClick ? 'underline' : 'none',
              textDecorationColor: accent + '66',
            }} onClick={e => { e.stopPropagation(); onStockClick && onStockClick(p.symbol); }}>
              {p.symbol}
            </span>

            {/* Sector pill */}
            {p.sector && (
              <span style={{ fontSize: '0.68rem', padding: '0.15rem 0.5rem', borderRadius: '999px', background: `${accent}14`, color: accent, fontWeight: 600 }}>
                {p.sector}
              </span>
            )}

            {/* Upside */}
            {p.upsidePct && (
              <span style={{ fontSize: '0.68rem', padding: '0.15rem 0.5rem', borderRadius: '999px', background: 'rgba(16,185,129,0.12)', color: '#34d399', fontWeight: 700 }}>
                {p.upsidePct}
              </span>
            )}

            {/* Dynamic discovery badge */}
            {p.isDynamic && <DiscoveryBadge />}
          </div>

          {/* Company name */}
          <div style={{ color: '#475569', fontSize: '0.74rem', marginTop: '0.1rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {p.name}
          </div>
        </div>

        <Sparkline score={p.score} trend={p.trend} />
        <div style={{ color: '#475569' }}>{isOpen ? <ChevronUp size={16}/> : <ChevronDown size={16}/>}</div>
        <ScoreRing score={p.score} />
      </div>

      {/* Expanded detail */}
      {isOpen && (
        <div style={{ borderTop: `1px solid ${accent}22`, padding: '1rem', background: 'rgba(0,0,0,0.2)' }}>

          {/* Signal pills */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginBottom: '0.85rem' }}>
            {SIGNAL_KEYS.map((key, idx) => signals[key]
              ? <Pill key={key} label={SIGNAL_LABELS[idx]} value={signals[key]} positive={p.signalPositive?.[idx] !== false} />
              : null
            )}
          </div>

          {/* Historical note — shown when history exists */}
          {p.historicalNote && p.historicalNote !== 'First run — no history yet' && (
            <div style={{ background: 'rgba(6,182,212,0.07)', border: '1px solid rgba(6,182,212,0.2)', borderRadius: '10px', padding: '0.6rem 0.9rem', marginBottom: '0.7rem', display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
              <Activity size={12} color="#22d3ee" style={{ flexShrink: 0, marginTop: 2 }}/>
              <p style={{ color: '#7dd3fc', fontSize: '0.78rem', lineHeight: 1.5, margin: 0 }}>
                <strong style={{ color: '#22d3ee' }}>Pattern: </strong>{p.historicalNote}
              </p>
            </div>
          )}

          {/* Why now */}
          <div style={{ background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.18)', borderRadius: '10px', padding: '0.75rem 1rem', marginBottom: '0.7rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.35rem' }}>
              <Zap size={12} color="#818cf8" />
              <span style={{ color: '#818cf8', fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Why Now</span>
            </div>
            <p style={{ color: '#cbd5e1', fontSize: '0.82rem', lineHeight: 1.65, margin: 0 }}>{p.whyNow}</p>
          </div>

          {/* Risk */}
          <div style={{ background: 'rgba(239,68,68,0.07)', border: '1px solid rgba(239,68,68,0.18)', borderRadius: '10px', padding: '0.65rem 1rem', display: 'flex', alignItems: 'flex-start', gap: '0.5rem', marginBottom: '0.75rem' }}>
            <AlertTriangle size={13} color="#f87171" style={{ flexShrink: 0, marginTop: 2 }}/>
            <p style={{ color: '#fca5a5', fontSize: '0.79rem', lineHeight: 1.5, margin: 0 }}>
              <strong>Risk: </strong>{p.risks}
            </p>
          </div>

          {onStockClick && (
            <button onClick={() => onStockClick(p.symbol)} style={{
              display: 'inline-flex', alignItems: 'center', gap: '0.35rem',
              background: 'transparent', border: `1px solid ${accent}40`,
              color: accent, borderRadius: '8px', padding: '0.42rem 0.9rem',
              cursor: 'pointer', fontSize: '0.76rem', fontWeight: 600,
            }}>
              <BarChart3 size={12}/> Full Analysis → {p.symbol}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// ── Log steps — updated for Option C timing ──────────────────
const LOG_STEPS = [
  { msg: '🤖 InvestorQ Top Winners Agent v3 initialized',                          step: null,      delay: 0 },
  { msg: '🎯 Building pool: 50 anchor stocks + fetching dynamic discoveries...',   step: 'pool',    delay: 800 },
  { msg: '📡 Batch 1-5: Anchor core — Tech & Healthcare (NVDA, LLY, MSFT...)',    step: 'av',      delay: 4000 },
  { msg: '📡 Batch 6-10: Anchor core — Financials & Consumer (JPM, V, WMT...)',   step: 'av',      delay: 12000 },
  { msg: '⚡ Batch 11-15: Alpha Intelligence™ scanning sentiment shifts...',       step: 'alpha',   delay: 20000 },
  { msg: '📊 Batch 16-20: Earnings quality — beat rates, EPS growth trends...',   step: 'earnings',delay: 28000 },
  { msg: '🔍 Batch 21+: Dynamic discoveries — live market movers being analyzed...',step: 'av',    delay: 36000 },
  { msg: '🧠 Loading history context from prior runs...',                           step: 'history', delay: 46000 },
  { msg: '✨ Sending signal matrix + history to Claude AI...',                      step: 'claude',  delay: 52000 },
  { msg: '⏳ Claude synthesizing patterns across anchor + dynamic stocks...',      step: 'claude',  delay: 60000 },
  { msg: '🏆 Ranking top 10 + sectors + surfacing discoveries...',                 step: 'output',  delay: 70000 },
];

// ── Main Component ────────────────────────────────────────────
const TopWinnersAgent = ({ onStockClick }) => {
  const [picks,        setPicks]        = useState([]);
  const [bySector,     setBySector]     = useState({});
  const [discoveries,  setDiscoveries]  = useState([]);
  const [loading,      setLoading]      = useState(false);
  const [expanded,     setExpanded]     = useState(null);
  const [logs,         setLogs]         = useState([]);
  const [meta,         setMeta]         = useState(null);
  const [error,        setError]        = useState(null);
  const [lastRun,      setLastRun]      = useState(null);
  const [cached,       setCached]       = useState(false);
  const [activeTab,    setActiveTab]    = useState('overall');
  const [journeyStep,  setJourneyStep]  = useState(null);
  const [showHowItWorks, setShowHowItWorks] = useState(false);
  const [historyData, setHistoryData] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [allSignals, setAllSignals] = useState([]);
  const logRef = useRef(null);

  const addLog = (msg, type = 'info') => setLogs(prev => [...prev, { msg, type, ts: Date.now() }]);

  const fetchHistory = async () => {
    setHistoryLoading(true);
    try {
      const [lbRaw, signalsRaw, runsRaw] = await Promise.all([
        fetch(`${API_URL}/top-winners/history/leaderboard`).then(r => r.ok ? r.json() : null),
        fetch(`${API_URL}/top-winners/history/signals/latest`).then(r => r.ok ? r.json() : null),
        fetch(`${API_URL}/top-winners/history`).then(r => r.ok ? r.json() : null),
      ]);

      // Normalize leaderboard — API returns snake_case, component expects camelCase
      const leaderboard = (lbRaw?.leaderboard || lbRaw || []).map(s => ({
        symbol:      s.symbol,
        sector:      s.sector || s.pool_type || '',
        poolType:    s.pool_type || 'anchor',
        appearances: parseInt(s.appearances) || 0,
        avgScore:    parseFloat(s.avg_score) || 0,
        avgRank:     parseFloat(s.avg_rank || s.avgRank || 5).toFixed(1),
        bestRank:    parseInt(s.best_rank || s.bestRank) || 10,
        worstRank:   parseInt(s.worst_rank || s.worstRank) || 10,
        trend:       s.trend || (parseFloat(s.avg_rank) < 3 ? 'rising' : 'stable'),
        lastSeen:    s.last_seen || s.lastSeen,
      }));

      // Normalize runs — API returns top10 as array of symbols, rank = array index+1
      const runs = (Array.isArray(runsRaw) ? runsRaw : runsRaw?.runs || runsRaw?.history || []).map(r => ({
        runId:       r.id || r.run_id || r.runId,
        generatedAt: r.run_at || r.generated_at || r.generatedAt || r.created_at,
        topSymbol:   r.top_symbol,
        topScore:    r.top_score,
        picks: (r.top10 || r.picks || []).map((item, idx) => ({
          symbol: typeof item === 'string' ? item : item.symbol,
          rank:   idx + 1,
          score:  typeof item === 'object' ? (item.score || 0) : 0,
        })),
      }));

      console.log('[TopWinners History] leaderboard:', leaderboard.length, 'stocks, runs:', runs.length);
      setHistoryData({ leaderboard, runs });
      if (signalsRaw?.signals) setAllSignals(signalsRaw.signals);
    } catch (e) {
      console.error('History fetch failed:', e);
    } finally {
      setHistoryLoading(false);
    }
  };

  // Auto-scroll log
  useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [logs]);

  // On mount: run agent (loads from cache) then fetch history
  useEffect(() => {
    runAgent(false);
    fetchHistory();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runAgent = async (forceRefresh = false) => {
    setLoading(true);
    setPicks([]); setBySector({}); setDiscoveries([]); setLogs([]);
    setExpanded(null); setError(null); setCached(false);
    setActiveTab('overall'); setJourneyStep(null);

    const timers = LOG_STEPS.map(step =>
      setTimeout(() => {
        addLog(step.msg, 'info');
        if (step.step) setJourneyStep(step.step);
      }, step.delay)
    );

    try {
      const url = `${API_URL}/top-winners${forceRefresh ? '?refresh=true' : ''}`;
      const res  = await fetch(url);
      if (!res.ok) throw new Error(`Server error ${res.status}`);
      const data = await res.json();

      timers.forEach(clearTimeout);
      setJourneyStep('output');

      const discCount = (data.discoveries || []).length;
      addLog(
        `✅ Complete — ${(data.picks || []).length} picks ranked, ${discCount} dynamic discover${discCount === 1 ? 'y' : 'ies'} made top 10`,
        'success'
      );

      setPicks(data.picks           || []);
      setBySector(data.bySector     || {});
      setDiscoveries(data.discoveries || []);
      setMeta(data.meta             || null);
      setCached(!!data._cached);
      setLastRun(new Date());
      fetchHistory();
    } catch (err) {
      timers.forEach(clearTimeout);
      addLog(`❌ Error: ${err.message}`, 'error');
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Which picks to show based on active tab
  const activePicks = activeTab === 'overall'
    ? picks
    : activeTab === 'discoveries'
    ? discoveries
    : (bySector[activeTab] || []).map((p, i) => ({ ...p, rank: i + 1 }));

  const sectorTabs = Object.keys(bySector);

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0a0f1e 0%, #0d1529 60%, #0a0f1e 100%)',
      padding: 'clamp(0.5rem, 3vw, 1.25rem)',
      fontFamily: "'DM Sans', -apple-system, sans-serif",
    }}>

      {/* ── Disclaimer banner ── */}
      <div style={{
        background: 'rgba(239,68,68,0.07)', border: '1px solid rgba(239,68,68,0.35)',
        borderLeft: '4px solid #ef4444', borderRadius: '10px',
        padding: '0.85rem 1rem', marginBottom: '1.25rem',
        display: 'flex', alignItems: 'flex-start', gap: '0.65rem',
      }}>
        <AlertTriangle size={18} color="#f87171" style={{ flexShrink: 0, marginTop: '0.1rem' }}/>
        <div>
          <p style={{ color: '#fca5a5', fontSize: '0.8rem', fontWeight: 700, margin: '0 0 0.2rem' }}>
            ⚠️ For Editorial, Research &amp; Educational Purposes Only — Not Investment Advice
          </p>
          <p style={{ color: '#94a3b8', fontSize: '0.72rem', lineHeight: 1.6, margin: 0 }}>
            InvestorQ is <strong style={{ color: '#fca5a5' }}>not a Registered Investment Advisor (RIA)</strong>, broker-dealer, or licensed financial advisor.
            AI-generated rankings are for <strong style={{ color: '#fca5a5' }}>informational and educational purposes only</strong>.
            Always consult a qualified financial professional before investing.
          </p>
        </div>
      </div>

      {/* ── Header ── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem', marginBottom: '1.25rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <div style={{ width: 46, height: 46, borderRadius: '13px', background: 'linear-gradient(135deg, #f59e0b, #ef4444)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 0 20px rgba(245,158,11,0.35)', flexShrink: 0 }}>
            <Trophy size={22} color="#fff"/>
          </div>
          <div>
            <h1 style={{ color: '#f1f5f9', fontSize: '1.4rem', fontWeight: 800, margin: 0, letterSpacing: '-0.02em' }}>
              InvestorQ Top Winners
            </h1>
            <p style={{ color: '#64748b', fontSize: '0.76rem', margin: 0 }}>
              AI Multi-Signal Agent · 50 anchor + dynamic discovery ·{' '}
              {lastRun ? `Last run: ${lastRun.toLocaleTimeString()}` : 'Not yet run'}
              {cached && <span style={{ color: '#6366f1', marginLeft: '0.5rem' }}>· cached</span>}
              {meta?.historyRuns > 0 && (
                <span style={{ color: '#22d3ee', marginLeft: '0.5rem' }}>· {meta.historyRuns} run history</span>
              )}
            </p>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <HowItWorksButton onClick={() => setShowHowItWorks(true)} />
          {picks.length > 0 && (
            <button onClick={() => runAgent(true)} disabled={loading} style={{
              display: 'flex', alignItems: 'center', gap: '0.45rem',
              padding: '0.6rem 1.1rem', borderRadius: '10px', cursor: 'pointer',
              background: 'rgba(255,255,255,0.05)', border: '1px solid #1e293b',
              color: '#fff', fontWeight: 700, fontSize: '0.85rem',
            }}>
              <RefreshCw size={14}/> Refresh
            </button>
          )}
          <button onClick={() => runAgent(false)} disabled={loading} style={{
            display: 'flex', alignItems: 'center', gap: '0.45rem',
            padding: '0.6rem 1.2rem', borderRadius: '10px',
            cursor: loading ? 'not-allowed' : 'pointer',
            background: loading ? 'rgba(99,102,241,0.3)' : 'linear-gradient(135deg, #6366f1, #8b5cf6)',
            border: 'none', color: '#fff', fontWeight: 700, fontSize: '0.85rem',
            boxShadow: loading ? 'none' : '0 4px 18px rgba(99,102,241,0.35)',
            opacity: loading ? 0.7 : 1,
          }}>
            {loading ? <Loader size={15} className="spin"/> : <Brain size={15}/>}
            {loading ? 'Analyzing...' : picks.length ? 'Re-run Agent' : 'Run AI Agent'}
          </button>
        </div>
      </div>

      {/* ── Journey map ── */}
      <JourneyMap activeStep={journeyStep} />

      {/* ── Log ── */}
      {logs.length > 0 && (
        <div ref={logRef} style={{
          background: 'rgba(0,0,0,0.45)', border: '1px solid #0f172a',
          borderRadius: '10px', padding: '0.7rem 1rem', marginBottom: '1.25rem',
          maxHeight: 95, overflowY: 'auto', fontFamily: 'monospace', fontSize: '0.72rem',
        }}>
          {logs.map((l, i) => (
            <div key={i} style={{
              color: l.type === 'success' ? '#34d399' : l.type === 'error' ? '#f87171' : '#94a3b8',
              lineHeight: '1.6', marginBottom: '0.1rem',
            }}>
              <span style={{ color: '#1e3a5f', marginRight: '0.6rem' }}>
                {new Date(l.ts).toLocaleTimeString('en', { hour12: false })}
              </span>
              {l.msg}
            </div>
          ))}
        </div>
      )}

      {/* ── Error ── */}
      {error && !loading && (
        <div style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: '10px', padding: '1rem', marginBottom: '1.25rem', display: 'flex', gap: '0.75rem' }}>
          <AlertTriangle size={16} color="#f87171"/>
          <span style={{ color: '#fca5a5', fontSize: '0.84rem' }}>{error}</span>
        </div>
      )}

      {/* ── Idle state ── */}
      {picks.length === 0 && !loading && !error && (
        <div style={{ background: 'rgba(15,23,42,0.8)', border: '1px dashed #1e3a5f', borderRadius: '16px', padding: '3rem 2rem', textAlign: 'center' }}>
          <Brain size={44} color="#6366f1" style={{ margin: '0 auto 1rem', opacity: 0.55 }}/>
          <h3 style={{ color: '#e2e8f0', fontWeight: 700, marginBottom: '0.5rem', fontSize: '1.1rem' }}>
            6-Signal AI Stock Analysis
          </h3>
          <p style={{ color: '#64748b', maxWidth: 500, margin: '0 auto 1.5rem', lineHeight: 1.65, fontSize: '0.85rem' }}>
            The agent builds a <strong style={{ color: '#94a3b8' }}>dynamic pool every run</strong> — 50 stable anchor stocks
            across all 11 sectors, plus 50 live market discoveries pulled fresh from today's movers.
            Claude ranks with full signal analysis <strong style={{ color: '#94a3b8' }}>and memory of every prior run</strong>,
            surfacing consistent performers and fresh breakout opportunities.
          </p>
          <div style={{ display: 'flex', justifyContent: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
            {['50 Anchor Stocks','50 Live Discoveries','Alpha Intelligence™','Earnings Quality','RSI Momentum','Run History Memory'].map(l => (
              <span key={l} style={{ background: 'rgba(99,102,241,0.1)', border: '1px solid rgba(99,102,241,0.22)', borderRadius: '999px', padding: '0.22rem 0.75rem', fontSize: '0.72rem', color: '#818cf8' }}>{l}</span>
            ))}
          </div>
        </div>
      )}

      {/* ── Meta bar ── */}
      {meta && picks.length > 0 && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: '1.25rem', flexWrap: 'wrap',
          padding: '0.6rem 1rem', marginBottom: '1rem',
          background: 'rgba(99,102,241,0.06)', border: '1px solid rgba(99,102,241,0.15)',
          borderRadius: '10px', fontSize: '0.74rem', color: '#64748b',
        }}>
          <span>
            <strong style={{ color: '#818cf8' }}>{meta.poolAnchorCount ?? '—'}</strong> anchor
            {' + '}
            <strong style={{ color: '#22d3ee' }}>{meta.poolDynamicCount ?? '—'}</strong> dynamic
            {' = '}
            <strong style={{ color: '#818cf8' }}>{meta.candidatesScanned}</strong> scanned
          </span>
          <span><strong style={{ color: '#818cf8' }}>{meta.signalLayers}</strong> signal layers</span>
          {meta.sectorCount > 0 && <span><strong style={{ color: '#818cf8' }}>{meta.sectorCount}</strong> sectors</span>}
          {meta.discoveriesCount > 0 && (
            <span><strong style={{ color: '#22d3ee' }}>{meta.discoveriesCount}</strong> discover{meta.discoveriesCount === 1 ? 'y' : 'ies'} in top 10</span>
          )}
          {meta.historyRuns > 0 && (
            <span><strong style={{ color: '#22d3ee' }}>{meta.historyRuns}</strong> prior runs analysed</span>
          )}
          {meta.consistentPicks?.length > 0 && (
            <span><strong style={{ color: '#f59e0b' }}>{meta.consistentPicks.length}</strong> consistent picks</span>
          )}
          <span><strong style={{ color: '#818cf8' }}>{(meta.processingMs / 1000).toFixed(1)}s</strong></span>
          <span><Clock size={11} style={{ marginRight: 3 }}/>{new Date(meta.generatedAt).toLocaleString()}</span>
        </div>
      )}

      {/* ── Tab bar ── */}
      {picks.length > 0 && (
        <div style={{ display: 'flex', gap: '0.4rem', marginBottom: '1rem', overflowX: 'auto', paddingBottom: '0.25rem' }}>

          {/* Overall */}
          <button onClick={() => setActiveTab('overall')} style={{
            padding: '0.45rem 1rem', borderRadius: '8px', cursor: 'pointer', whiteSpace: 'nowrap',
            background: activeTab === 'overall' ? 'rgba(99,102,241,0.2)' : 'rgba(255,255,255,0.04)',
            border: activeTab === 'overall' ? '1px solid rgba(99,102,241,0.4)' : '1px solid #1e293b',
            color: activeTab === 'overall' ? '#818cf8' : '#64748b', fontSize: '0.78rem', fontWeight: 700,
          }}>
            🏆 Top 10 Overall
          </button>

          {/* Discoveries tab — only shown if any dynamic stocks made top 10 */}
          {discoveries.length > 0 && (
            <button onClick={() => setActiveTab('discoveries')} style={{
              padding: '0.45rem 1rem', borderRadius: '8px', cursor: 'pointer', whiteSpace: 'nowrap',
              background: activeTab === 'discoveries' ? 'rgba(6,182,212,0.15)' : 'rgba(255,255,255,0.04)',
              border: activeTab === 'discoveries' ? '1px solid rgba(6,182,212,0.4)' : '1px solid #1e293b',
              color: activeTab === 'discoveries' ? '#22d3ee' : '#64748b', fontSize: '0.78rem', fontWeight: 700,
              display: 'flex', alignItems: 'center', gap: '0.35rem',
            }}>
              <Compass size={12}/> Discoveries ({discoveries.length})
            </button>
          )}

          {/* Sector tabs */}
          {sectorTabs.map(sector => (
            <button key={sector} onClick={() => setActiveTab(sector)} style={{
              padding: '0.45rem 1rem', borderRadius: '8px', cursor: 'pointer', whiteSpace: 'nowrap',
              background: activeTab === sector ? 'rgba(245,158,11,0.15)' : 'rgba(255,255,255,0.04)',
              border: activeTab === sector ? '1px solid rgba(245,158,11,0.35)' : '1px solid #1e293b',
              color: activeTab === sector ? '#f59e0b' : '#64748b', fontSize: '0.78rem', fontWeight: 600,
            }}>
              {sector}
            </button>
          ))}
        </div>
      )}

      {/* ── Section label ── */}
      {picks.length > 0 && (
        <div style={{ marginBottom: '0.75rem' }}>
          {activeTab === 'overall' && (
            <p style={{ color: '#475569', fontSize: '0.76rem', margin: 0 }}>
              Top 10 highest-conviction picks across all sectors — max 3 from any single sector
              {meta?.historyRuns > 0 && <span style={{ color: '#6366f1' }}> · ranked with {meta.historyRuns}-run historical memory</span>}
            </p>
          )}
          {activeTab === 'discoveries' && (
            <p style={{ color: '#475569', fontSize: '0.76rem', margin: 0 }}>
              <span style={{ color: '#22d3ee' }}>Dynamic discoveries</span> — stocks the agent found in live market data this run that weren't on the static watchlist
            </p>
          )}
          {activeTab !== 'overall' && activeTab !== 'discoveries' && (
            <p style={{ color: '#475569', fontSize: '0.76rem', margin: 0 }}>
              Best pick in <strong style={{ color: '#f59e0b' }}>{activeTab}</strong> — highest signal strength this run
            </p>
          )}
        </div>
      )}

      {/* ── Results ── */}
      {activePicks.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.65rem' }}>
          {activePicks.map((p, i) => (
            <StockCard
              key={p.symbol}
              p={p}
              i={i}
              expanded={expanded}
              setExpanded={setExpanded}
              onStockClick={onStockClick}
            />
          ))}
        </div>
      )}

      {/* ── Consistent picks note — shown after enough history ── */}
      {meta?.consistentPicks?.length > 0 && activeTab === 'overall' && (
        <div style={{
          marginTop: '1rem', padding: '0.75rem 1rem',
          background: 'rgba(99,102,241,0.06)', border: '1px solid rgba(99,102,241,0.18)',
          borderRadius: '10px', display: 'flex', alignItems: 'center', gap: '0.6rem',
        }}>
          <Database size={14} color="#818cf8" style={{ flexShrink: 0 }}/>
          <p style={{ color: '#64748b', fontSize: '0.74rem', margin: 0 }}>
            <strong style={{ color: '#818cf8' }}>Consistent across runs:</strong>{' '}
            {meta.consistentPicks.join(', ')} — these stocks have appeared in the top 10 in the majority of prior runs.
          </p>
        </div>
      )}

      {/* ── History Intelligence Section ── */}
      {picks.length > 0 && (
        <PortfolioLens picks={picks} allSignals={allSignals} onStockClick={onStockClick} />
      )}

      {picks.length > 0 && (
        <PortfolioSimulator picks={picks} allSignals={allSignals} onStockClick={onStockClick} />
      )}

      {picks.length > 0 && (
        <HistoryIntelligence
          historyData={historyData}
          loading={historyLoading}
          picks={picks}
          onStockClick={onStockClick}
        />
      )}

      {/* ── Full disclaimer ── */}
      {(picks.length > 0 || !loading) && (
        <div style={{ marginTop: '1.5rem', background: 'rgba(239,68,68,0.05)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: '10px', padding: '1rem 1.1rem', display: 'flex', alignItems: 'flex-start', gap: '0.6rem' }}>
          <Shield size={15} color="#f87171" style={{ flexShrink: 0, marginTop: 2 }}/>
          <div>
            <p style={{ color: '#fca5a5', fontSize: '0.75rem', fontWeight: 700, margin: '0 0 0.35rem' }}>
              Editorial Research &amp; Educational Disclaimer
            </p>
            <p style={{ color: '#64748b', fontSize: '0.7rem', lineHeight: 1.7, margin: 0 }}>
              <strong style={{ color: '#94a3b8' }}>InvestorQ is not a Registered Investment Advisor (RIA), broker-dealer, or licensed financial advisor.</strong>{' '}
              The Top Winners list is generated by an AI editorial research agent synthesizing publicly available market data including earnings history,
              price momentum, news sentiment, and analyst estimates. It is produced solely for <strong style={{ color: '#94a3b8' }}>editorial, research, and educational purposes</strong> and
              does <strong style={{ color: '#94a3b8' }}>not</strong> constitute investment advice, a securities recommendation, a solicitation, or an offer to buy
              or sell any security or financial instrument. Rankings and scores are algorithmic outputs and may be inaccurate, incomplete, or outdated.
              Past performance is not indicative of future results. All investing involves substantial risk, including possible loss of all principal invested.
              Always perform your own independent research and consult a qualified, licensed financial advisor before making any investment decision.
              InvestorQ, Offbeet Media &amp; Tech Inc., and their affiliates accept no liability for any financial losses arising from use of this tool.
            </p>
          </div>
        </div>
      )}

      {showHowItWorks && <HowItWorksModal onClose={() => setShowHowItWorks(false)} />}

      <style>{`
        @keyframes spin { from { transform: rotate(0deg) } to { transform: rotate(360deg) } }
        .spin { animation: spin 1s linear infinite }
      `}</style>
    </div>
  );
};

// ── History Intelligence Component ──────────────────────────────────────────
const CHART_COLORS = ['#6366f1','#10b981','#f59e0b','#f472b6','#22d3ee','#a78bfa','#fb923c','#34d399'];

const HistoryIntelligence = ({ historyData, loading, picks, onStockClick }) => {
  const [selected, setSelected] = useState(null);
  const lb = historyData?.leaderboard || [];
  const runs = historyData?.runs || [];
  const totalRuns = runs.length;

  const topStocks = lb.slice(0, 10);


  // Auto-select first stock
  const sel = selected || topStocks[0]?.symbol;
  const selLb = lb.find(s => s.symbol === sel);

  // Build rank per run per stock from top10 arrays
  const trajectory = {};
  [...runs].reverse().forEach((run, ri) => {
    (run.picks || []).forEach(p => {
      if (!trajectory[p.symbol]) trajectory[p.symbol] = [];
      trajectory[p.symbol][ri] = p.rank;
    });
  });

  // Date labels newest-last
  const dateLabels = [...runs].reverse().map(r =>
    new Date(r.generatedAt).toLocaleDateString('en', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
  );

  if (loading) return (
    <div style={{ marginTop: '1.5rem', padding: '2rem', textAlign: 'center', background: 'rgba(15,23,42,0.6)', border: '1px solid #1e293b', borderRadius: '14px' }}>
      <Loader size={18} color="#6366f1" className="spin" style={{ display: 'block', margin: '0 auto 0.5rem' }}/>
      <p style={{ color: '#475569', fontSize: '0.78rem', margin: 0 }}>Loading historical intelligence...</p>
    </div>
  );

  if (!lb.length && !runs.length) return null;

  // Events for selected stock
  const selTraj = trajectory[sel] || [];
  const events = [];
  selTraj.forEach((rank, i) => {
    const prev = selTraj[i - 1];
    const date = dateLabels[i] || `Run ${i+1}`;
    if (i === 0) events.push({ type: 'entry', rank, date, note: `First appearance in top 10` });
    else if (prev === undefined && rank) events.push({ type: 'entry', rank, date, note: `Re-entered top 10` });
    else if (rank !== undefined && prev !== undefined) {
      if (rank < prev - 2) events.push({ type: 'rise', rank, date, note: `Rose from #${prev} to #${rank}` });
      else if (rank > prev + 2) events.push({ type: 'drop', rank, date, note: `Dropped from #${prev} to #${rank}` });
      if (rank === 1 && prev !== 1) events.push({ type: 'surge', rank, date, note: `Reached #1 position` });
    }
  });

  const isInCurrent = picks.some(p => p.symbol === sel);
  const consistency = selLb ? Math.min(120, Math.round((parseInt(selLb.appearances) / totalRuns) * 100)) : 0;

  return (
    <div style={{ marginTop: '1.5rem', display: 'flex', flexDirection: 'column', gap: '0.875rem' }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <Activity size={14} color="#818cf8"/>
        <span style={{ color: '#818cf8', fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
          Historical Intelligence — {totalRuns} runs · {lb.length} stocks tracked
        </span>
      </div>

      {/* Main chart card */}
      <div style={{ background: 'rgba(10,15,30,0.8)', border: '1px solid rgba(99,102,241,0.15)', borderRadius: '14px', overflow: 'hidden' }}>

        {/* Chart header */}
        <div style={{ padding: '1rem 1.25rem 0.75rem', borderBottom: '1px solid rgba(99,102,241,0.1)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.6rem' }}>
            <span style={{ color: '#f1f5f9', fontSize: '0.82rem', fontWeight: 700 }}>Rank trajectory</span>
            <span style={{ color: '#334155', fontSize: '0.68rem' }}>Lower = better</span>
          </div>
          {/* Stock selector pills */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem' }}>
            {topStocks.map((s, i) => {
              const isActive = sel === s.symbol;
              const c = CHART_COLORS[i];
              return (
                <button key={s.symbol} onClick={() => setSelected(s.symbol)} style={{
                  display: 'flex', alignItems: 'center', gap: '0.3rem',
                  padding: '0.2rem 0.55rem', borderRadius: '999px', cursor: 'pointer',
                  background: isActive ? `${c}22` : 'transparent',
                  border: `1px solid ${isActive ? c : c + '44'}`,
                  transition: 'all 0.15s',
                }}>
                  <div style={{ width: 7, height: 7, borderRadius: '50%', background: c, flexShrink: 0, opacity: isActive ? 1 : 0.5 }}/>
                  <span style={{ fontSize: '0.7rem', fontWeight: 700, color: isActive ? c : '#475569' }}>{s.symbol}</span>
                  <span style={{ fontSize: '0.62rem', color: '#334155' }}>#{s.avgRank}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* SVG Chart */}
        <div style={{ padding: '0.75rem 1rem 0.5rem', overflowX: 'auto' }}>
          <svg viewBox={`0 0 ${Math.max(400, runs.length * 38 + 60)} 160`} style={{ width: '100%', minWidth: 320, display: 'block' }}>
            {/* Grid lines */}
            {[1,3,5,7,10].map(rank => {
              const y = 10 + ((rank - 1) / 9) * 120;
              return (
                <g key={rank}>
                  <line x1="32" y1={y} x2={Math.max(400, runs.length * 38 + 60) - 8} y2={y} stroke="#1e293b" strokeWidth="1" strokeDasharray={rank === 1 ? '0' : '3,4'}/>
                  <text x="28" y={y + 4} fontSize="9" fill="#334155" textAnchor="end">#{rank}</text>
                </g>
              );
            })}
            {/* Stock lines */}
            {topStocks.map((s, si) => {
              const traj = trajectory[s.symbol] || [];
              const c = CHART_COLORS[si];
              const isActive = sel === s.symbol;
              const pts = traj.map((rank, i) => {
                if (rank === undefined) return null;
                const x = 40 + i * 38;
                const y = 10 + ((rank - 1) / 9) * 120;
                return [x, y];
              }).filter(Boolean);
              if (pts.length < 1) return null;
              const d = pts.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p[0]} ${p[1]}`).join(' ');
              return (
                <g key={s.symbol}>
                  <path d={d} fill="none" stroke={c} strokeWidth={isActive ? 2.5 : 1.2} strokeLinejoin="round" strokeLinecap="round" opacity={isActive ? 1 : 0.3}/>
                  {isActive && pts.map((p, i) => (
                    <circle key={i} cx={p[0]} cy={p[1]} r="4" fill={c} stroke="rgba(10,15,30,0.9)" strokeWidth="2"/>
                  ))}
                </g>
              );
            })}
            {/* Date labels */}
            {dateLabels.map((d, i) => (
              <text key={i} x={40 + i * 38} y="155" fontSize="8" fill="#1e3a5f" textAnchor="middle">{d.split(',')[0]}</text>
            ))}
          </svg>
        </div>
      </div>

      {/* Deep dive + leaderboard row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '0.875rem' }}>

        {/* Selected stock deep dive */}
        {sel && selLb && (
          <div style={{ background: 'rgba(10,15,30,0.8)', border: '1px solid rgba(99,102,241,0.15)', borderRadius: '14px', padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.7rem' }}>

            {/* Header row */}
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.15rem' }}>
                  <span style={{ fontSize: '1.1rem', fontWeight: 800, color: '#f1f5f9' }}>{sel}</span>
                  {isInCurrent && <span style={{ fontSize: '0.6rem', color: '#10b981', background: 'rgba(16,185,129,0.12)', padding: '0.1rem 0.45rem', borderRadius: '999px', fontWeight: 700 }}>live</span>}
                </div>
                <span style={{ fontSize: '0.68rem', color: '#334155' }}>{selLb.sector} · {selLb.poolType}</span>
              </div>
              <button onClick={() => onStockClick && onStockClick(sel)} style={{
                padding: '0.3rem 0.8rem', background: 'rgba(99,102,241,0.12)',
                border: '1px solid rgba(99,102,241,0.25)', borderRadius: '6px',
                color: '#818cf8', fontSize: '0.7rem', fontWeight: 700, cursor: 'pointer',
              }}>Analyse →</button>
            </div>

            {/* 5-stat grid */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(54px, 1fr))', gap: '0.35rem' }}>
              {[
                { l: 'In top 10', v: `${selLb.appearances}/${totalRuns}`, c: '#10b981' },
                { l: 'Avg rank', v: `#${selLb.avgRank}`, c: '#6366f1' },
                { l: 'Best', v: `#${selLb.bestRank}`, c: '#ffd700' },
                { l: 'Worst', v: `#${selLb.worstRank || '—'}`, c: '#f87171' },
                { l: 'Score', v: selLb.avgScore ? `${selLb.avgScore}` : '—', c: '#f59e0b' },
              ].map(s => (
                <div key={s.l} style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid #1e293b', borderRadius: '7px', padding: '0.45rem 0.4rem', textAlign: 'center' }}>
                  <div style={{ fontSize: '0.56rem', color: '#334155', marginBottom: '0.2rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{s.l}</div>
                  <div style={{ fontSize: '0.85rem', fontWeight: 800, color: s.c }}>{s.v}</div>
                </div>
              ))}
            </div>

            {/* Consistency bar */}
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.3rem' }}>
                <span style={{ fontSize: '0.62rem', color: '#334155', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Consistency score</span>
                <span style={{ fontSize: '0.7rem', fontWeight: 700, color: consistency >= 100 ? '#10b981' : consistency >= 80 ? '#f59e0b' : '#94a3b8' }}>{consistency}%</span>
              </div>
              <div style={{ height: 5, background: '#0f172a', borderRadius: '999px', overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${Math.min(100, consistency)}%`, background: consistency >= 100 ? '#10b981' : consistency >= 80 ? '#f59e0b' : '#6366f1', borderRadius: '999px', transition: 'width 0.6s ease' }}/>
              </div>
            </div>

            {/* Rank history mini sparkline */}
            {selTraj.filter(r => r !== undefined).length > 1 && (
              <div>
                <div style={{ fontSize: '0.62rem', color: '#334155', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.35rem' }}>Rank per run (newest right)</div>
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: '3px', height: 36 }}>
                  {selTraj.map((rank, i) => {
                    if (rank === undefined) return <div key={i} style={{ flex: 1, height: 3, background: '#1e293b', borderRadius: '2px' }}/>;
                    const h = Math.max(6, Math.round(((11 - rank) / 10) * 36));
                    const c = rank <= 3 ? '#10b981' : rank <= 6 ? '#6366f1' : '#f59e0b';
                    return (
                      <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end' }}>
                        <div style={{ width: '100%', height: h, background: c, borderRadius: '2px 2px 0 0', opacity: 0.8 }}/>
                        <span style={{ fontSize: '0.48rem', color: '#334155', marginTop: 1 }}>#{rank}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Events */}
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '0.62rem', color: '#334155', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.35rem' }}>Key events</div>
              {events.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.28rem' }}>
                  {events.slice(-6).map((ev, i) => (
                    <div key={i} style={{
                      display: 'flex', alignItems: 'center', gap: '0.45rem',
                      padding: '0.3rem 0.55rem', borderRadius: '6px',
                      background: ev.type === 'entry' ? 'rgba(16,185,129,0.06)' : ev.type === 'surge' ? 'rgba(253,224,71,0.06)' : ev.type === 'rise' ? 'rgba(99,102,241,0.06)' : 'rgba(239,68,68,0.06)',
                      border: `1px solid ${ev.type === 'entry' ? 'rgba(16,185,129,0.12)' : ev.type === 'surge' ? 'rgba(253,224,71,0.12)' : ev.type === 'rise' ? 'rgba(99,102,241,0.12)' : 'rgba(239,68,68,0.12)'}`,
                    }}>
                      <span style={{ fontSize: '0.65rem', flexShrink: 0 }}>{ev.type === 'entry' ? '🟢' : ev.type === 'surge' ? '⭐' : ev.type === 'rise' ? '📈' : '📉'}</span>
                      <span style={{ fontSize: '0.68rem', color: '#94a3b8', flex: 1 }}>{ev.note}</span>
                      <span style={{ fontSize: '0.6rem', color: '#334155', flexShrink: 0 }}>{ev.date.split(',')[0]}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p style={{ fontSize: '0.68rem', color: '#334155', margin: 0 }}>No significant rank changes detected yet.</p>
              )}
            </div>

            {/* AI insight from existing run — no extra Claude call needed */}
            {(() => {
              const pick = picks.find(p => p.symbol === sel);
              if (!pick) return null;
              return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                  {pick.whyNow && (
                    <div style={{ background: 'rgba(99,102,241,0.05)', border: '1px solid rgba(99,102,241,0.15)', borderRadius: '8px', padding: '0.65rem 0.75rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.35rem' }}>
                        <Brain size={11} color="#818cf8"/>
                        <span style={{ fontSize: '0.6rem', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700 }}>Why now</span>
                      </div>
                      <p style={{ fontSize: '0.72rem', color: '#94a3b8', margin: 0, lineHeight: 1.6 }}>{pick.whyNow}</p>
                    </div>
                  )}
                  {pick.signals && (
                    <div style={{ background: 'rgba(15,23,42,0.5)', border: '1px solid #1e293b', borderRadius: '8px', padding: '0.65rem 0.75rem' }}>
                      <div style={{ fontSize: '0.6rem', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700, marginBottom: '0.35rem' }}>Signal breakdown</div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
                        {Object.entries(pick.signals).map(([k, v]) => (
                          <div key={k} style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
                            <span style={{ fontSize: '0.62rem', color: '#334155', minWidth: 70, textTransform: 'capitalize', flexShrink: 0 }}>{k}</span>
                            <span style={{ fontSize: '0.68rem', color: '#64748b', lineHeight: 1.4 }}>{v}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {pick.risks && (
                    <div style={{ background: 'rgba(239,68,68,0.04)', border: '1px solid rgba(239,68,68,0.12)', borderRadius: '8px', padding: '0.55rem 0.75rem', display: 'flex', gap: '0.4rem', alignItems: 'flex-start' }}>
                      <AlertTriangle size={11} color="#f87171" style={{ flexShrink: 0, marginTop: 2 }}/>
                      <p style={{ fontSize: '0.68rem', color: '#94a3b8', margin: 0, lineHeight: 1.5 }}>{pick.risks}</p>
                    </div>
                  )}
                  {!pick.whyNow && (
                    <p style={{ fontSize: '0.68rem', color: '#334155', margin: 0 }}>
                      Run the agent to see AI analysis for {sel}.
                    </p>
                  )}
                </div>
              );
            })()}
          </div>
        )}

        {/* Leaderboard */}
        <div style={{ background: 'rgba(10,15,30,0.8)', border: '1px solid rgba(99,102,241,0.15)', borderRadius: '14px', padding: '1rem' }}>
          <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#94a3b8', marginBottom: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            Consistency leaderboard
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
            {lb.map((s, i) => {
              const pct = Math.min(120, Math.round((parseInt(s.appearances) / totalRuns) * 100));
              const isSel = sel === s.symbol;
              const inCurrent = picks.some(p => p.symbol === s.symbol);
              const c = CHART_COLORS[i] || '#475569';
              const trend = parseFloat(s.avgRank) < 3 ? 'rising' : parseFloat(s.avgRank) < 6 ? 'stable' : 'falling';
              return (
                <div key={s.symbol} onClick={() => setSelected(s.symbol)} style={{
                  display: 'flex', alignItems: 'center', gap: '0.5rem',
                  padding: '0.45rem 0.6rem', borderRadius: '8px', cursor: 'pointer',
                  background: isSel ? 'rgba(99,102,241,0.1)' : 'rgba(255,255,255,0.02)',
                  border: `1px solid ${isSel ? 'rgba(99,102,241,0.3)' : 'transparent'}`,
                  transition: 'all 0.15s',
                }}>
                  <span style={{ fontSize: '0.65rem', fontWeight: 700, color: c, minWidth: 16 }}>#{i+1}</span>
                  <span style={{ fontSize: '0.78rem', fontWeight: 800, color: '#f1f5f9', minWidth: 44 }}>{s.symbol}</span>
                  {inCurrent && <div style={{ width: 5, height: 5, borderRadius: '50%', background: '#10b981', flexShrink: 0 }}/>}
                  <div style={{ flex: 1, height: 4, background: '#0f172a', borderRadius: '999px', overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${Math.min(100, pct)}%`, background: c, borderRadius: '999px', opacity: 0.7 }}/>
                  </div>
                  <span style={{ fontSize: '0.65rem', color: '#475569', minWidth: 28, textAlign: 'right' }}>{pct}%</span>
                  <span style={{ fontSize: '0.62rem', color: trend === 'rising' ? '#10b981' : trend === 'falling' ? '#f87171' : '#475569', minWidth: 14 }}>
                    {trend === 'rising' ? '↑' : trend === 'falling' ? '↓' : '→'}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Fleeting stocks — appeared 1-3 times */}
      {lb.filter(s => parseInt(s.appearances) <= 3).length > 0 && (
        <div style={{ background: 'rgba(10,15,30,0.8)', border: '1px solid rgba(6,182,212,0.15)', borderRadius: '14px', padding: '1rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
            <Compass size={13} color="#22d3ee"/>
            <span style={{ fontSize: '0.72rem', fontWeight: 700, color: '#22d3ee', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Fleeting appearances — entered &amp; dropped
            </span>
            <span style={{ fontSize: '0.65rem', color: '#334155', marginLeft: 'auto' }}>1-3 runs only</span>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginBottom: '0.6rem' }}>
            {lb.filter(s => parseInt(s.appearances) <= 3).map(s => {
              const inCurrent = picks.some(p => p.symbol === s.symbol);
              return (
                <button key={s.symbol} onClick={() => setSelected(s.symbol)} style={{
                  display: 'flex', alignItems: 'center', gap: '0.35rem',
                  padding: '0.28rem 0.6rem', borderRadius: '7px', cursor: 'pointer',
                  background: inCurrent ? 'rgba(16,185,129,0.08)' : 'rgba(6,182,212,0.06)',
                  border: `1px solid ${inCurrent ? 'rgba(16,185,129,0.25)' : 'rgba(6,182,212,0.18)'}`,
                  transition: 'all 0.15s',
                }}>
                  <span style={{ fontSize: '0.74rem', fontWeight: 800, color: inCurrent ? '#10b981' : '#22d3ee' }}>{s.symbol}</span>
                  <span style={{ fontSize: '0.62rem', color: '#334155' }}>{s.appearances}× · avg #{s.avgRank}</span>
                  {inCurrent && <span style={{ width: 5, height: 5, borderRadius: '50%', background: '#10b981', display: 'inline-block' }}/>}
                </button>
              );
            })}
          </div>
          <p style={{ fontSize: '0.68rem', color: '#334155', margin: 0, lineHeight: 1.5 }}>
            Broke into top 10 briefly — worth watching if signals strengthen again.
          </p>
        </div>
      )}
    </div>
  );
};


// ── Portfolio Lens ────────────────────────────────────────────────────────────
const PROFILES = [
  {
    key: 'diversified',
    label: 'Diversified',
    icon: PieChart,
    color: '#6366f1',
    dim: 'rgba(99,102,241,0.08)',
    border: 'rgba(99,102,241,0.2)',
    desc: 'Balanced exposure — mix of sectors, moderate beta, solid fundamentals',
    criteria: (p) => {
      let score = 0;
      // Sector diversity reward — penalise tech concentration
      const growthSectors = ['Technology', 'Communication'];
      const defensiveSectors = ['Healthcare', 'Consumer', 'Energy', 'Financials', 'Industrials', 'Materials', 'Utilities'];
      if (defensiveSectors.some(s => (p.sector||'').includes(s))) score += 3;
      if (growthSectors.some(s => (p.sector||'').includes(s))) score += 1;
      // Moderate beta — reward 0.6-1.2
      if (p.beta) {
        if (p.beta >= 0.6 && p.beta <= 1.2) score += 3;
        else if (p.beta > 1.2 && p.beta <= 1.5) score += 1;
      }
      // Decent upside without being speculative
      if (p.upsidePct > 5 && p.upsidePct < 30) score += 2;
      // Solid earnings
      if (p.beatRate >= 60) score += 2;
      if (p.beatRate >= 75) score += 1;
      // Positive EPS growth
      if (p.epsGrowthYoYPct > 0) score += 1;
      // Not overbought momentum
      if (p.rsiZone === 'neutral' || p.rsiZone === 'bullish') score += 1;
      return score;
    }
  },
  {
    key: 'aggressive',
    label: 'Aggressive',
    icon: Flame,
    color: '#f59e0b',
    dim: 'rgba(245,158,11,0.08)',
    border: 'rgba(245,158,11,0.2)',
    desc: 'Maximum growth — high upside, tech heavy, strong momentum',
    criteria: (p) => {
      let score = 0;
      // High upside is the primary signal
      if (p.upsidePct > 10) score += 2;
      if (p.upsidePct > 20) score += 2;
      if (p.upsidePct > 35) score += 2;
      // Growth sectors
      const growthSectors = ['Technology', 'Communication', 'Consumer Discretionary', 'Healthcare'];
      if (growthSectors.some(s => (p.sector||'').includes(s))) score += 2;
      // Strong EPS growth — any positive growth counts
      if (p.epsGrowthYoYPct > 0) score += 1;
      if (p.epsGrowthYoYPct > 10) score += 2;
      if (p.epsGrowthYoYPct > 30) score += 2;
      // Bullish momentum signals
      if (p.rsiZone === 'bullish') score += 2;
      if (p.rsiZone === 'oversold') score += 1; // contrarian entry
      if (p.sentimentShift === 'improving') score += 2;
      if (p.bullishPct > 60) score += 1;
      // Discovery stocks = higher risk/reward
      if (p.poolType === 'discovery') score += 2;
      // Special catalysts
      if (p.specialFlags?.merger || p.specialFlags?.insider) score += 2;
      // High beat rate shows execution
      if (p.beatRate >= 70) score += 1;
      return score;
    }
  },
  {
    key: 'conservative',
    label: 'Conservative',
    icon: Briefcase,
    color: '#10b981',
    dim: 'rgba(16,185,129,0.08)',
    border: 'rgba(16,185,129,0.2)',
    desc: 'Capital preservation — low volatility, reliable earnings, defensive sectors',
    criteria: (p) => {
      let score = 0;
      // Defensive sectors are primary signal
      const defensiveSectors = ['Healthcare', 'Consumer Staples', 'Consumer Defensive', 'Utilities', 'Energy'];
      const financialSectors = ['Financials', 'Financial Services'];
      if (defensiveSectors.some(s => (p.sector||'').includes(s))) score += 4;
      if (financialSectors.some(s => (p.sector||'').includes(s))) score += 2;
      // Low beta = low volatility
      if (p.beta && p.beta < 1.0) score += 3;
      if (p.beta && p.beta < 0.8) score += 2;
      // Earnings reliability is critical
      if (p.beatRate >= 70) score += 3;
      if (p.beatRate >= 85) score += 2;
      // Reasonable valuation
      if (p.peRatio && p.peRatio < 30) score += 2;
      if (p.peRatio && p.peRatio < 20) score += 1;
      // Stable upside — not too high (speculative) or negative
      if (p.upsidePct > 3 && p.upsidePct < 25) score += 2;
      // Established companies only
      if (p.poolType === 'anchor') score += 1;
      // Not overbought
      if (p.rsiZone !== 'overbought') score += 1;
      return score;
    }
  }
];

const PortfolioLens = ({ picks, allSignals = [], onStockClick }) => {
  const [active, setActive] = useState('diversified');
  if (!picks || picks.length === 0) return null;

  const profile = PROFILES.find(p => p.key === active);

  // Use full historical signal universe if available, else fall back to current picks
  // allSignals has all 29+ unique stocks ever seen with their latest signal data
  const universe = allSignals.length > 0 ? allSignals : picks;
  // Mark which stocks are in the current top 10
  const currentSymbols = new Set(picks.map(p => p.symbol));

  // Score ALL stocks for ALL 3 profiles simultaneously
  const scoreKey = { diversified: '_d', aggressive: '_a', conservative: '_c' };
  const allScored = universe.map(p => ({
    ...p,
    _isCurrentPick: currentSymbols.has(p.symbol),
    _d: PROFILES[0].criteria(p),
    _a: PROFILES[1].criteria(p),
    _c: PROFILES[2].criteria(p),
  }));

  // Rank ALL picks by active profile score
  const ranked = [...allScored].sort((a, b) => b[scoreKey[active]] - a[scoreKey[active]]);

  // Best profile for each pick
  const bestProfileFor = (p) => {
    const scores = { diversified: p._d, aggressive: p._a, conservative: p._c };
    return Object.entries(scores).sort((a,b) => b[1]-a[1])[0][0];
  };

  // Max possible score for normalization (run criteria with a perfect mock stock)
  const maxRaw = Math.max(...allScored.map(p => p[scoreKey[active]]), 1);

  // Fit level: high / medium / low based on score relative to max
  const fitLevel = (score) => {
    const pct = score / maxRaw;
    if (pct >= 0.65) return { label: 'Strong fit',   color: '#10b981', bg: 'rgba(16,185,129,0.10)' };
    if (pct >= 0.35) return { label: 'Moderate fit', color: '#f59e0b', bg: 'rgba(245,158,11,0.10)' };
    return              { label: 'Lower match',    color: '#f87171', bg: 'rgba(239,68,68,0.07)'  };
  };

  // Rank badge style — gold/silver/bronze for top 3, neutral after
  const rankBadgeStyle = (i) => {
    if (i === 0) return { bg: 'rgba(255,215,0,0.15)',   border: 'rgba(255,215,0,0.5)',   color: '#fbbf24' };
    if (i === 1) return { bg: 'rgba(192,192,192,0.15)', border: 'rgba(192,192,192,0.4)', color: '#94a3b8' };
    if (i === 2) return { bg: 'rgba(205,127,50,0.15)',  border: 'rgba(205,127,50,0.4)',  color: '#cd7f32' };
    return               { bg: 'rgba(255,255,255,0.04)', border: '#1e293b',               color: '#475569' };
  };

  // Overall alignment = % of picks that are strong/moderate fit
  const goodFits = allScored.filter(p => p[scoreKey[active]] / maxRaw >= 0.35).length;
  const alignPct = Math.round((goodFits / allScored.length) * 100);

  // Sector distribution across all picks ranked by this profile
  const sectorMap = {};
  ranked.slice(0, 5).forEach(p => { sectorMap[p.sector] = (sectorMap[p.sector] || 0) + 1; });

  const SECTOR_COLORS = {
    'Technology': '#6366f1', 'Healthcare': '#10b981', 'Financials': '#f59e0b',
    'Energy': '#f97316', 'Consumer': '#ec4899', 'Communication': '#8b5cf6',
    'Industrials': '#64748b', 'Materials': '#06b6d4', 'Utilities': '#84cc16',
    'Real Estate': '#a78bfa', 'Unknown': '#334155',
  };
  const sectorColor = (s) => Object.entries(SECTOR_COLORS).find(([k]) => (s||'').includes(k))?.[1] || '#334155';

  return (
    <div style={{ marginTop: '1.5rem', display: 'flex', flexDirection: 'column', gap: '0.875rem' }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <Target size={14} color="#818cf8"/>
        <span style={{ color: '#818cf8', fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
          Portfolio Lens — How picks align with your strategy
        </span>
      </div>

      {/* Profile tabs */}
      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
        {PROFILES.map(pr => {
          const Icon = pr.icon;
          const isSel = active === pr.key;
          return (
            <button key={pr.key} onClick={() => setActive(pr.key)} style={{
              display: 'flex', alignItems: 'center', gap: '0.4rem',
              padding: '0.45rem 0.9rem', borderRadius: '8px', cursor: 'pointer',
              background: isSel ? pr.dim : 'rgba(15,23,42,0.6)',
              border: `1px solid ${isSel ? pr.color : '#1e293b'}`,
              transition: 'all 0.15s',
            }}>
              <Icon size={12} color={isSel ? pr.color : '#475569'}/>
              <span style={{ fontSize: '0.75rem', fontWeight: isSel ? 700 : 500, color: isSel ? pr.color : '#475569' }}>{pr.label}</span>
            </button>
          );
        })}
      </div>

      {/* Main card */}
      <div style={{ background: 'rgba(10,15,30,0.8)', border: `1px solid ${profile.border}`, borderRadius: '14px', padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.875rem' }}>

        {/* Profile description + alignment meter */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: '0.72rem', color: profile.color, fontWeight: 700, marginBottom: '0.2rem' }}>{profile.label} Investor</div>
            <div style={{ fontSize: '0.68rem', color: '#475569' }}>{profile.desc}</div>
          </div>
          <div style={{ textAlign: 'right', flexShrink: 0 }}>
            <div style={{ fontSize: '0.6rem', color: '#334155', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.25rem' }}>Portfolio alignment</div>
            <div style={{ fontSize: '1.4rem', fontWeight: 800, color: alignPct >= 70 ? profile.color : alignPct >= 40 ? '#f59e0b' : '#f87171', lineHeight: 1 }}>{alignPct}%</div>
          </div>
        </div>

        {/* Alignment bar */}
        <div style={{ height: 5, background: '#0f172a', borderRadius: '999px', overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${alignPct}%`, background: `linear-gradient(90deg, ${profile.color}, ${profile.color}aa)`, borderRadius: '999px', transition: 'width 0.6s ease' }}/>
        </div>

        {/* Fit legend */}
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', paddingBottom: '0.5rem', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
          {[
            { color: '#10b981', label: 'Strong fit (65%+)' },
            { color: '#f59e0b', label: 'Moderate fit (35–64%)' },
            { color: '#f87171', label: 'Lower match (<35%)' },
          ].map(l => (
            <div key={l.label} style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: l.color, flexShrink: 0 }}/>
              <span style={{ fontSize: '0.65rem', color: '#64748b' }}>{l.label}</span>
            </div>
          ))}
        </div>

        {/* All picks ranked by this profile */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '0.5rem' }}>
          {ranked.map((p, i) => {
            const fit          = fitLevel(p[scoreKey[active]]);
            const best         = bestProfileFor(p);
            const isTopProfile = best === active;
            const barPct       = Math.round((p[scoreKey[active]] / maxRaw) * 100);
            const badge        = rankBadgeStyle(i);

            return (
              <button key={p.symbol} onClick={() => onStockClick && onStockClick(p.symbol)} style={{
                display: 'flex', flexDirection: 'column', gap: '0.45rem',
                padding: '0.6rem 0.75rem', borderRadius: '10px', textAlign: 'left',
                background: isTopProfile ? `${profile.color}0d` : 'rgba(255,255,255,0.02)',
                border: `1px solid ${isTopProfile ? profile.color + '35' : '#1e293b'}`,
                cursor: 'pointer', transition: 'border-color 0.15s',
              }}>

                {/* Top row: rank badge + ticker info + score number */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>

                  {/* Rank badge — gold/silver/bronze for top 3 */}
                  <div style={{
                    width: 30, height: 30, borderRadius: '7px', flexShrink: 0,
                    background: badge.bg, border: `1px solid ${badge.border}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <span style={{ fontSize: '0.62rem', fontWeight: 700, color: badge.color, lineHeight: 1 }}>
                      #{i + 1}
                    </span>
                  </div>

                  {/* Ticker + badges */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: '0.85rem', fontWeight: 800, color: '#f1f5f9' }}>{p.symbol}</span>
                      {p._isCurrentPick && (
                        <span style={{
                          fontSize: '0.55rem', fontWeight: 700, padding: '0.1rem 0.35rem',
                          borderRadius: '4px', background: 'rgba(16,185,129,0.15)',
                          color: '#10b981', border: '1px solid rgba(16,185,129,0.3)',
                        }}>Top 10</span>
                      )}
                      <span style={{
                        fontSize: '0.58rem', padding: '0.1rem 0.35rem', borderRadius: '4px',
                        background: `${sectorColor(p.sector)}18`, color: sectorColor(p.sector),
                        whiteSpace: 'nowrap',
                      }}>
                        {(p.sector || 'Unknown').split(' ')[0]}
                      </span>
                      {p.appearances > 0 && (
                        <span style={{ fontSize: '0.55rem', color: '#475569', marginLeft: 'auto', whiteSpace: 'nowrap' }}>
                          {p.appearances}+ runs
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Score number — right side */}
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{ fontSize: '1rem', fontWeight: 800, color: fit.color, lineHeight: 1 }}>
                      {barPct}
                    </div>
                    <div style={{ fontSize: '0.52rem', color: '#334155', lineHeight: 1.2 }}>/100</div>
                  </div>
                </div>

                {/* Fit bar row */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                  <div style={{ flex: 1, height: 4, background: '#0f172a', borderRadius: '999px', overflow: 'hidden' }}>
                    <div style={{
                      height: '100%', width: `${barPct}%`,
                      background: fit.color, borderRadius: '999px',
                      transition: 'width 0.4s ease',
                    }}/>
                  </div>
                  <span style={{
                    fontSize: '0.6rem', fontWeight: 600, color: fit.color,
                    whiteSpace: 'nowrap', flexShrink: 0, minWidth: 72, textAlign: 'right',
                  }}>
                    {fit.label}
                  </span>
                </div>

                {/* Best-fit redirect — only when this profile is NOT the best */}
                {!isTopProfile && (
                  <div style={{ fontSize: '0.58rem', color: PROFILES.find(pr => pr.key === best)?.color || '#475569' }}>
                    Best fit → {best}
                  </div>
                )}

              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};


// ── Portfolio Simulator ───────────────────────────────────────────────────────
// Fetches REAL adjusted close prices per stock via /api/stocks/:symbol/price-history
// Builds equal-weight blended P&L curves from actual market data.
// Historical (past) + projected forward (sector CAGR from today).
// Rate-limit safe: fetches one profile's stocks sequentially, caches in state.

const SIM_PROJ_CAGR = {
  'Technology': 0.18, 'Communication': 0.12, 'Healthcare': 0.10,
  'Financials': 0.12, 'Financial Services': 0.12, 'Financial': 0.12,
  'Consumer Discretionary': 0.10, 'Consumer Staples': 0.07, 'Consumer': 0.09,
  'Energy': 0.08, 'Industrials': 0.10, 'Materials': 0.09,
  'Utilities': 0.06, 'Real Estate': 0.07, 'Basic': 0.09, 'Unknown': 0.09,
};
const simProjCAGR = (sector) => {
  if (!sector) return SIM_PROJ_CAGR['Unknown'];
  const k = Object.keys(SIM_PROJ_CAGR).find(k => sector.includes(k));
  return k ? SIM_PROJ_CAGR[k] : SIM_PROJ_CAGR['Unknown'];
};

// S&P 500 forward CAGR assumption
const BENCH_FWD_CAGR = 0.105;

const SIM_HORIZONS = [
  { key: '6m',  label: '6 Mo',    months: 6,  period: '6m'  },
  { key: '1y',  label: '1 Year',  months: 12, period: '1y'  },
  { key: '2y',  label: '2 Years', months: 24, period: '2y'  },
  { key: '5y',  label: '5 Years', months: 60, period: '5y'  },
];

const SIM_INITIAL = 10000;

const SIM_SEC_COLORS = {
  'Technology': '#6366f1', 'Healthcare': '#10b981', 'Financials': '#f59e0b',
  'Financial':  '#f59e0b', 'Energy': '#f97316',  'Consumer': '#ec4899',
  'Communication': '#8b5cf6', 'Industrials': '#64748b', 'Materials': '#06b6d4',
  'Utilities': '#84cc16',  'Real Estate': '#a78bfa', 'Basic': '#06b6d4',
  'Unknown': '#334155',
};
const simSecCol = (s) => SIM_SEC_COLORS[s] || '#475569';

const SIM_PROF_META = {
  diversified:  { label: 'Diversified',  color: '#6366f1', dim: 'rgba(99,102,241,0.1)',  border: 'rgba(99,102,241,0.3)'  },
  aggressive:   { label: 'Aggressive',   color: '#f59e0b', dim: 'rgba(245,158,11,0.1)',  border: 'rgba(245,158,11,0.3)'  },
  conservative: { label: 'Conservative', color: '#10b981', dim: 'rgba(16,185,129,0.1)',  border: 'rgba(16,185,129,0.3)'  },
};

// Resample an array of {date, adjustedClose} to monthly last-close points
const toMonthly = (prices) => {
  const byMonth = {};
  prices.forEach(p => {
    const key = p.date.slice(0, 7); // 'YYYY-MM'
    byMonth[key] = p.adjustedClose;
  });
  return Object.entries(byMonth)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([month, close]) => ({ month, close }));
};

// Blend equal-weight monthly series from multiple stocks' monthly arrays.
// Returns array of { month, value } where value starts at SIM_INITIAL.
const blendSeries = (stockMonthlyMap, months) => {
  const symbols = Object.keys(stockMonthlyMap);
  if (symbols.length === 0) return [];

  // Build a common month list (intersection of all stocks that have data)
  const allMonths = new Set();
  symbols.forEach(sym => stockMonthlyMap[sym].forEach(p => allMonths.add(p.month)));
  const sortedMonths = [...allMonths].sort().slice(-months - 1); // last N+1 months

  if (sortedMonths.length < 2) return [];

  const baseMonth = sortedMonths[0];

  // For each stock, get its price at baseMonth and each subsequent month
  const stockBases = {};
  symbols.forEach(sym => {
    const series = stockMonthlyMap[sym];
    const base = series.find(p => p.month === baseMonth);
    stockBases[sym] = base ? base.close : null;
  });

  const validSymbols = symbols.filter(s => stockBases[s] !== null && stockBases[s] > 0);
  if (validSymbols.length === 0) return [];

  return sortedMonths.map((month, mi) => {
    // Average normalised return across all valid stocks
    let totalReturn = 0;
    let count = 0;
    validSymbols.forEach(sym => {
      const series = stockMonthlyMap[sym];
      const pt = series.find(p => p.month === month);
      if (pt && stockBases[sym]) {
        totalReturn += pt.close / stockBases[sym];
        count++;
      }
    });
    const avgReturn = count > 0 ? totalReturn / count : 1;
    return { month, value: SIM_INITIAL * avgReturn };
  });
};

// Build S&P 500 benchmark series aligned to the same month list
const buildBenchSeries = (monthList) => {
  if (!monthList || monthList.length === 0) return [];
  return monthList.map((m, i) => ({
    month: m.month,
    value: SIM_INITIAL * Math.pow(1 + BENCH_FWD_CAGR / 12, i),
  }));
};

// Build forward projection series starting from today's value
const buildForwardSeries = (startValue, sector, forwardMonths) => {
  const r = simProjCAGR(sector) / 12;
  return Array.from({ length: forwardMonths + 1 }, (_, i) => ({
    projected: true,
    value: startValue * Math.pow(1 + r, i),
  }));
};

// Convert a value series to an SVG polyline points string
const seriesToPoints = (series, W, H, minV, maxV, xOffset = 0) => {
  if (!series || series.length === 0) return '';
  const range = maxV - minV || 1;
  return series.map((pt, i) => {
    const x = xOffset + (i / (series.length - 1)) * W;
    const y = H - ((pt.value - minV) / range) * H;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(' ');
};

const PortfolioSimulator = ({ picks, allSignals = [], onStockClick }) => {
  const [activeHorizon, setActiveHorizon] = useState('1y');
  const [activeProfile, setActiveProfile] = useState('diversified');
  // priceCache: { [symbol]: { prices: [{date,adjustedClose}] } }
  const [priceCache,   setPriceCache]   = useState({});
  const [loadingSyms,  setLoadingSyms]  = useState(new Set());
  const [fetchError,   setFetchError]   = useState(null);
  const [animKey,      setAnimKey]      = useState(0);

  // --- Derive strong-fit stocks (safe to compute before early return) ---
  const universe = (allSignals.length > 0 ? allSignals : picks) || [];
  const scored = universe.map(p => ({
    ...p,
    _d: PROFILES[0].criteria(p),
    _a: PROFILES[1].criteria(p),
    _c: PROFILES[2].criteria(p),
  }));
  const getStrongFits = (key) => {
    const mx = Math.max(...scored.map(p => p[key]), 1);
    return scored.filter(p => p[key] >= mx * 0.65).sort((a, b) => b[key] - a[key]);
  };
  const allProfileStocks = {
    diversified:  getStrongFits('_d'),
    aggressive:   getStrongFits('_a'),
    conservative: getStrongFits('_c'),
  };
  const stocks = allProfileStocks[activeProfile];

  // --- useEffect MUST be called before any early return (Rules of Hooks) ---
  // Fetch real prices for all stocks in the active profile
  // Sequential with 220ms gap to respect AV 5 req/min free tier
  useEffect(() => {
    if (!picks || picks.length === 0) return;
    if (!stocks || stocks.length === 0) return;
    const missing = stocks.filter(s => !priceCache[s.symbol] && !loadingSyms.has(s.symbol));
    if (missing.length === 0) return;

    setFetchError(null);
    const newLoading = new Set([...loadingSyms, ...missing.map(s => s.symbol)]);
    setLoadingSyms(newLoading);

    const fetchSequential = async () => {
      for (const stock of missing) {
        try {
          const res  = await fetch(`${API_URL}/stocks/${stock.symbol}/price-history?period=5y`);
          if (!res.ok) throw new Error(`${res.status}`);
          const data = await res.json();
          setPriceCache(prev => ({ ...prev, [stock.symbol]: data }));
        } catch (e) {
          console.warn(`[Simulator] Failed to fetch ${stock.symbol}:`, e.message);
        } finally {
          setLoadingSyms(prev => { const s = new Set(prev); s.delete(stock.symbol); return s; });
        }
        await new Promise(r => setTimeout(r, 220));
      }
    };
    fetchSequential();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeProfile, picks, stocks.map(s => s.symbol).join(',')]);

  // --- Early return AFTER all hooks ---
  if (!picks || picks.length === 0) return null;

  const pm      = SIM_PROF_META[activeProfile];
  const horizon = SIM_HORIZONS.find(h => h.key === activeHorizon);

  // Trigger chart re-animation when profile or horizon changes
  const handleSwitch = (profileKey, horizonKey) => {
    setAnimKey(k => k + 1);
    if (profileKey  !== undefined) setActiveProfile(profileKey);
    if (horizonKey  !== undefined) setActiveHorizon(horizonKey);
  };

  // Build monthly price maps for stocks that have loaded data
  const stockMonthlyMap = {};
  stocks.forEach(s => {
    const cached = priceCache[s.symbol];
    if (cached?.prices?.length > 0) {
      stockMonthlyMap[s.symbol] = toMonthly(cached.prices);
    }
  });

  const loadedCount  = Object.keys(stockMonthlyMap).length;
  const pendingCount = stocks.filter(s => loadingSyms.has(s.symbol)).length;
  const hasData      = loadedCount > 0;

  // Build blended historical series
  const historicalSeries = hasData
    ? blendSeries(stockMonthlyMap, horizon.months)
    : [];

  // Build forward projection from end of historical data
  const lastHistVal    = historicalSeries.length > 0
    ? historicalSeries[historicalSeries.length - 1].value : SIM_INITIAL;
  const elapsedMonths  = historicalSeries.length;
  const remainMonths   = Math.max(0, horizon.months - elapsedMonths + 1);

  // Average sector for projection blending
  const avgSector = stocks[0]?.sector || 'Unknown';
  const forwardSeries = remainMonths > 0
    ? buildForwardSeries(lastHistVal, avgSector, remainMonths)
    : [];

  // Benchmark — full horizon
  const dummyMonths = Array.from({ length: horizon.months + 1 }, (_, i) => ({ month: `M${i}`, value: 0 }));
  const benchSeries = buildBenchSeries(dummyMonths);

  // Summary stats
  const endHist   = historicalSeries.length > 0 ? historicalSeries[historicalSeries.length - 1].value : SIM_INITIAL;
  const endFwd    = forwardSeries.length > 0    ? forwardSeries[forwardSeries.length - 1].value    : endHist;
  const endBench  = benchSeries.length > 0      ? benchSeries[benchSeries.length - 1].value        : SIM_INITIAL;
  const totalEnd  = hasData ? endFwd : SIM_INITIAL;
  const totalGain = totalEnd - SIM_INITIAL;
  const gainPct   = (totalGain / SIM_INITIAL) * 100;
  const vsBench   = totalEnd - endBench;
  const vsBenchPct = (vsBench / SIM_INITIAL) * 100;

  // Chart
  const CW = 520, CH = 140;
  const allVals  = [
    ...historicalSeries.map(p => p.value),
    ...forwardSeries.map(p => p.value),
    ...benchSeries.map(p => p.value),
    SIM_INITIAL,
  ];
  const minV = Math.min(...allVals) * 0.99;
  const maxV = Math.max(...allVals) * 1.01;
  const range = maxV - minV || 1;

  // Separate historical + forward x-positions
  const totalPts  = historicalSeries.length + Math.max(0, forwardSeries.length - 1);
  const histW     = totalPts > 1 ? (historicalSeries.length / totalPts) * CW : CW * 0.6;
  const fwdW      = CW - histW;

  const toPoint = (val, idx, total, xStart, width) => {
    const x = xStart + (total > 1 ? (idx / (total - 1)) * width : width);
    const y = CH - ((val - minV) / range) * CH;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  };

  const histPoints = historicalSeries.map((p, i) =>
    toPoint(p.value, i, historicalSeries.length, 0, histW)
  ).join(' ');

  // Forward starts from last historical point
  const fwdOffsetX = histW;
  const fwdPoints  = forwardSeries.map((p, i) =>
    toPoint(p.value, i, forwardSeries.length, fwdOffsetX, fwdW)
  ).join(' ');

  const benchPoints = benchSeries.map((p, i) =>
    toPoint(p.value, i, benchSeries.length, 0, CW)
  ).join(' ');

  // Y-axis grid
  const yGridVals = Array.from({ length: 5 }, (_, i) => minV + (range / 4) * i);

  // Sector breakdown
  const sectorMap = {};
  stocks.forEach(s => {
    const sec = (s.sector || 'Unknown').split(' ')[0];
    sectorMap[sec] = (sectorMap[sec] || 0) + 1;
  });
  const sectorEntries = Object.entries(sectorMap).sort((a, b) => b[1] - a[1]);

  const fmt    = (n) => (n >= 0 ? '+$' : '-$') + Math.abs(Math.round(n)).toLocaleString('en-US');
  const fmtPct = (n) => (n >= 0 ? '+' : '') + n.toFixed(1) + '%';
  const fmtVal = (n) => '$' + Math.round(n).toLocaleString('en-US');

  // Quick horizon summary (use historical data if available, else forward-only)
  const horizonSummary = (hKey) => {
    const h   = SIM_HORIZONS.find(x => x.key === hKey);
    const hs  = hasData ? blendSeries(stockMonthlyMap, h.months) : [];
    const end = hs.length > 0 ? hs[hs.length - 1].value : SIM_INITIAL * Math.pow(1 + simProjCAGR(avgSector) / 12, h.months);
    return { end, pct: ((end - SIM_INITIAL) / SIM_INITIAL) * 100 };
  };

  return (
    <div style={{ marginTop: '1.5rem', display: 'flex', flexDirection: 'column', gap: '0.875rem' }}>

      {/* ── Header ── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <TrendingUp size={14} color="#818cf8"/>
          <span style={{ color: '#818cf8', fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
            Portfolio Simulator — Real Price History + Forward Projection
          </span>
        </div>
        <span style={{ fontSize: '0.62rem', color: '#475569' }}>
          Strong-fit stocks only · equal-weight · $10k base · live price data
        </span>
      </div>

      {/* ── Profile tabs ── */}
      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
        {Object.entries(SIM_PROF_META).map(([key, meta]) => {
          const Icon = key === 'diversified' ? PieChart : key === 'aggressive' ? Flame : Briefcase;
          const isSel = activeProfile === key;
          const cnt   = allProfileStocks[key].length;
          return (
            <button key={key} onClick={() => handleSwitch(key, undefined)} style={{
              display: 'flex', alignItems: 'center', gap: '0.5rem',
              padding: '0.5rem 1rem', borderRadius: '8px', cursor: 'pointer',
              background: isSel ? meta.dim : 'rgba(15,23,42,0.6)',
              border: `1px solid ${isSel ? meta.color : '#1e293b'}`,
              transition: 'all 0.15s',
            }}>
              <Icon size={12} color={isSel ? meta.color : '#475569'}/>
              <span style={{ fontSize: '0.78rem', fontWeight: isSel ? 700 : 500, color: isSel ? meta.color : '#475569' }}>{meta.label}</span>
              <span style={{
                fontSize: '0.6rem', padding: '0.1rem 0.4rem', borderRadius: '999px',
                background: isSel ? `${meta.color}25` : 'rgba(255,255,255,0.05)',
                color: isSel ? meta.color : '#334155', fontWeight: 600,
              }}>{cnt} picks</span>
            </button>
          );
        })}
      </div>

      {/* ── Main card ── */}
      <div style={{ background: 'rgba(10,15,30,0.8)', border: `1px solid ${pm.border}`, borderRadius: '14px', overflow: 'hidden' }}>

        {/* ── Horizon strip ── */}
        <div style={{ display: 'flex', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
          {SIM_HORIZONS.map(h => {
            const isSel = activeHorizon === h.key;
            const summ  = horizonSummary(h.key);
            const pos   = summ.pct >= 0;
            return (
              <button key={h.key} onClick={() => handleSwitch(undefined, h.key)} style={{
                flex: 1, padding: '0.75rem 0.5rem', textAlign: 'center', cursor: 'pointer',
                background: isSel ? `${pm.color}12` : 'transparent',
                borderRight: '1px solid rgba(255,255,255,0.04)',
                borderBottom: isSel ? `2px solid ${pm.color}` : '2px solid transparent',
                transition: 'all 0.15s',
              }}>
                <div style={{ fontSize: '0.65rem', color: isSel ? pm.color : '#475569', fontWeight: 700, marginBottom: '0.25rem' }}>{h.label}</div>
                <div style={{ fontSize: '0.92rem', fontWeight: 800, color: pos ? '#10b981' : '#f87171', lineHeight: 1 }}>
                  {fmtPct(summ.pct)}
                </div>
                <div style={{ fontSize: '0.58rem', color: '#334155', marginTop: '0.15rem' }}>
                  {fmtVal(summ.end)}
                </div>
              </button>
            );
          })}
        </div>

        <div style={{ padding: '1rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>

          {/* ── Loading status bar ── */}
          {(pendingCount > 0 || (!hasData && stocks.length > 0)) && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: '0.6rem',
              padding: '0.5rem 0.75rem', borderRadius: '8px',
              background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.2)',
            }}>
              <Loader size={12} color="#818cf8" className="spin" style={{ flexShrink: 0 }}/>
              <span style={{ fontSize: '0.7rem', color: '#818cf8' }}>
                Loading real price data — {loadedCount}/{stocks.length} stocks loaded
                {pendingCount > 0 && ` · ${pendingCount} fetching…`}
              </span>
              <div style={{ flex: 1, height: 3, background: '#0f172a', borderRadius: '999px', overflow: 'hidden' }}>
                <div style={{
                  height: '100%', borderRadius: '999px', background: '#6366f1',
                  width: `${stocks.length > 0 ? (loadedCount / stocks.length) * 100 : 0}%`,
                  transition: 'width 0.4s ease',
                }}/>
              </div>
            </div>
          )}

          {/* ── Big stat row ── */}
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem', flexWrap: 'wrap', justifyContent: 'space-between' }}>
            <div>
              <div style={{ fontSize: '0.6rem', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.3rem' }}>
                {pm.label} · {horizon.label} · {stocks.length} strong-fit stocks
              </div>
              <div style={{ fontSize: '2rem', fontWeight: 800, color: totalGain >= 0 ? '#10b981' : '#f87171', lineHeight: 1, letterSpacing: '-0.02em' }}>
                {fmtVal(totalEnd)}
              </div>
              <div style={{ fontSize: '0.82rem', fontWeight: 700, color: totalGain >= 0 ? '#10b981' : '#f87171', marginTop: '0.2rem' }}>
                {fmt(totalGain)} &nbsp;({fmtPct(gainPct)})
              </div>
              {!hasData && stocks.length > 0 && (
                <div style={{ fontSize: '0.62rem', color: '#475569', marginTop: '0.2rem' }}>
                  Loading real prices…
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: '0.6rem' }}>
              <div style={{
                padding: '0.6rem 0.85rem', borderRadius: '10px', minWidth: 110, textAlign: 'center',
                background: vsBench >= 0 ? 'rgba(16,185,129,0.08)' : 'rgba(239,68,68,0.08)',
                border: `1px solid ${vsBench >= 0 ? 'rgba(16,185,129,0.22)' : 'rgba(239,68,68,0.22)'}`,
              }}>
                <div style={{ fontSize: '0.58rem', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.25rem' }}>vs S&amp;P 500</div>
                <div style={{ fontSize: '1.1rem', fontWeight: 800, color: vsBench >= 0 ? '#10b981' : '#f87171', lineHeight: 1 }}>
                  {vsBench >= 0 ? '+' : ''}{fmtPct(vsBenchPct)}
                </div>
                <div style={{ fontSize: '0.6rem', color: '#475569', marginTop: '0.2rem' }}>SPY → {fmtVal(endBench)}</div>
              </div>
              <div style={{
                padding: '0.6rem 0.85rem', borderRadius: '10px', minWidth: 90, textAlign: 'center',
                background: 'rgba(255,255,255,0.03)', border: '1px solid #1e293b',
              }}>
                <div style={{ fontSize: '0.58rem', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.25rem' }}>Invested</div>
                <div style={{ fontSize: '1.1rem', fontWeight: 800, color: '#f1f5f9', lineHeight: 1 }}>$10,000</div>
                <div style={{ fontSize: '0.6rem', color: '#334155', marginTop: '0.2rem' }}>equal-weight</div>
              </div>
            </div>
          </div>

          {/* ── Chart ── */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
              <span style={{ fontSize: '0.6rem', color: '#334155', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Real price history + forward projection
              </span>
              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                  <div style={{ width: 14, height: 2.5, background: pm.color, borderRadius: 2 }}/>
                  <span style={{ fontSize: '0.58rem', color: '#64748b' }}>Historical</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                  <div style={{ width: 14, height: 0, borderTop: `2px dashed ${pm.color}`, opacity: 0.6 }}/>
                  <span style={{ fontSize: '0.58rem', color: '#64748b' }}>Projected</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                  <svg width="14" height="8"><line x1="0" y1="4" x2="14" y2="4" stroke="#475569" strokeWidth="1.5" strokeDasharray="3,2"/></svg>
                  <span style={{ fontSize: '0.58rem', color: '#475569' }}>S&amp;P 500</span>
                </div>
              </div>
            </div>

            {!hasData && stocks.length > 0 ? (
              // Skeleton chart while loading
              <div style={{
                height: CH + 28, background: 'rgba(255,255,255,0.02)', borderRadius: '8px',
                border: '1px solid #1e293b', display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <div style={{ textAlign: 'center' }}>
                  <Loader size={20} color="#6366f1" className="spin" style={{ display: 'block', margin: '0 auto 0.5rem' }}/>
                  <span style={{ fontSize: '0.72rem', color: '#475569' }}>
                    Fetching real price data for {stocks.length} stocks…
                  </span>
                </div>
              </div>
            ) : stocks.length === 0 ? (
              <div style={{ height: 80, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <span style={{ fontSize: '0.72rem', color: '#475569' }}>No strong-fit stocks for this profile. Run the agent to populate picks.</span>
              </div>
            ) : (
              <svg
                key={`sim-${activeProfile}-${activeHorizon}-${animKey}-${loadedCount}`}
                viewBox={`0 0 ${CW + 46} ${CH + 28}`}
                style={{ width: '100%', display: 'block', overflow: 'visible' }}
              >
                <defs>
                  <style>{`
                    @keyframes simDraw { from { stroke-dashoffset: 4000; } to { stroke-dashoffset: 0; } }
                    .sim-hist  { stroke-dasharray: 4000; animation: simDraw 1.0s ease-out forwards; }
                    .sim-fwd   { stroke-dasharray: 4000; animation: simDraw 1.2s ease-out forwards; }
                    .sim-bench { stroke-dasharray: 4000; animation: simDraw 1.4s ease-out forwards; }
                  `}</style>
                </defs>

                {/* Y-axis grid lines */}
                {yGridVals.map((v, gi) => {
                  const yp = CH - ((v - minV) / range) * CH;
                  const isBase = Math.abs(v - SIM_INITIAL) < range * 0.08;
                  return (
                    <g key={gi}>
                      <line x1={0} y1={yp} x2={CW} y2={yp}
                        stroke={isBase ? 'rgba(255,255,255,0.12)' : 'rgba(255,255,255,0.04)'}
                        strokeWidth={isBase ? 1 : 0.5}
                        strokeDasharray={isBase ? '0' : '3,5'}
                      />
                      <text x={CW + 4} y={yp + 4} fontSize="9" fill="#334155">
                        {v >= 1000 ? `$${(v / 1000).toFixed(0)}k` : `$${Math.round(v)}`}
                      </text>
                    </g>
                  );
                })}

                {/* Today separator line */}
                {historicalSeries.length > 0 && forwardSeries.length > 1 && (
                  <>
                    <line x1={histW} y1={0} x2={histW} y2={CH}
                      stroke="rgba(255,255,255,0.15)" strokeWidth="1" strokeDasharray="3,4"/>
                    <text x={histW + 3} y={10} fontSize="8" fill="#475569">Today</text>
                  </>
                )}

                {/* Benchmark dashed line */}
                {benchPoints && (
                  <polyline points={benchPoints} fill="none" stroke="#475569" strokeWidth="1.2"
                    strokeDasharray="4,3" className="sim-bench" strokeLinecap="round"/>
                )}

                {/* Historical solid line */}
                {histPoints && (
                  <polyline points={histPoints} fill="none" stroke={pm.color} strokeWidth="2.5"
                    className="sim-hist" strokeLinecap="round"
                    style={{ filter: `drop-shadow(0 0 4px ${pm.color}55)` }}/>
                )}

                {/* Forward projection dashed line */}
                {fwdPoints && forwardSeries.length > 1 && (
                  <polyline points={fwdPoints} fill="none" stroke={pm.color} strokeWidth="1.8"
                    strokeDasharray="5,4" className="sim-fwd" strokeLinecap="round" opacity="0.7"/>
                )}

                {/* End-point dot */}
                {(histPoints || fwdPoints) && (() => {
                  const lastSeries = forwardSeries.length > 1 ? forwardSeries : historicalSeries;
                  const lastVal    = lastSeries.length > 0 ? lastSeries[lastSeries.length - 1].value : null;
                  if (!lastVal) return null;
                  const dotY = CH - ((lastVal - minV) / range) * CH;
                  return <circle cx={CW} cy={dotY.toFixed(1)} r="4.5" fill={pm.color} stroke="rgba(10,15,30,0.9)" strokeWidth="2.5"/>;
                })()}

                {/* X-axis labels */}
                {['Now', horizon.label].map((lbl, i) => (
                  <text key={i} x={i === 0 ? 0 : CW} y={CH + 18}
                    fontSize="8" fill="#334155" textAnchor={i === 0 ? 'start' : 'end'}>
                    {lbl}
                  </text>
                ))}
                {historicalSeries.length > 0 && forwardSeries.length > 1 && (
                  <text x={histW} y={CH + 18} fontSize="8" fill="#475569" textAnchor="middle">
                    {new Date().toLocaleDateString('en', { month: 'short', year: '2-digit' })}
                  </text>
                )}
              </svg>
            )}
          </div>

          {/* ── Per-stock performance table ── */}
          {hasData && (
            <div>
              <div style={{ fontSize: '0.62rem', color: '#334155', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.5rem' }}>
                Individual stock performance — {horizon.label}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(145px, 1fr))', gap: '0.35rem' }}>
                {stocks.map(s => {
                  const sec      = (s.sector || 'Unknown').split(' ')[0];
                  const cached   = priceCache[s.symbol];
                  const loading  = loadingSyms.has(s.symbol);
                  let ret        = null;

                  if (cached?.prices?.length > 0) {
                    const monthly  = toMonthly(cached.prices);
                    const pts      = monthly.slice(-horizon.months - 1);
                    if (pts.length >= 2) {
                      ret = ((pts[pts.length - 1].close - pts[0].close) / pts[0].close) * 100;
                    }
                  }

                  return (
                    <button key={s.symbol} onClick={() => onStockClick && onStockClick(s.symbol)} style={{
                      display: 'flex', alignItems: 'center', gap: '0.4rem',
                      padding: '0.45rem 0.55rem', borderRadius: '8px', cursor: 'pointer',
                      background: 'rgba(255,255,255,0.02)', border: '1px solid #1e293b',
                      textAlign: 'left', transition: 'border-color 0.15s',
                    }}>
                      <div style={{ width: 7, height: 7, borderRadius: '50%', background: simSecCol(sec), flexShrink: 0 }}/>
                      <span style={{ fontSize: '0.78rem', fontWeight: 700, color: '#f1f5f9', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {s.symbol}
                      </span>
                      {loading ? (
                        <Loader size={10} color="#6366f1" className="spin"/>
                      ) : ret !== null ? (
                        <span style={{ fontSize: '0.68rem', fontWeight: 700, color: ret >= 0 ? '#10b981' : '#f87171', flexShrink: 0 }}>
                          {fmtPct(ret)}
                        </span>
                      ) : (
                        <span style={{ fontSize: '0.62rem', color: '#334155' }}>—</span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── Sector exposure ── */}
          {sectorEntries.length > 0 && (
            <div>
              <div style={{ fontSize: '0.62rem', color: '#334155', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.5rem' }}>
                Sector exposure
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
                {sectorEntries.map(([sec, count]) => {
                  const pct = stocks.length > 0 ? Math.round((count / stocks.length) * 100) : 0;
                  return (
                    <div key={sec} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <div style={{ width: 7, height: 7, borderRadius: '50%', background: simSecCol(sec), flexShrink: 0 }}/>
                      <span style={{ fontSize: '0.65rem', color: '#64748b', minWidth: 88 }}>{sec}</span>
                      <div style={{ flex: 1, height: 4, background: '#0f172a', borderRadius: '999px', overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${pct}%`, background: simSecCol(sec), borderRadius: '999px', opacity: 0.8, transition: 'width 0.4s ease' }}/>
                      </div>
                      <span style={{ fontSize: '0.62rem', color: '#475569', minWidth: 28, textAlign: 'right' }}>{pct}%</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── Disclaimer ── */}
          <div style={{
            padding: '0.6rem 0.85rem', borderRadius: '8px',
            background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.18)',
            display: 'flex', alignItems: 'flex-start', gap: '0.5rem',
          }}>
            <AlertTriangle size={12} color="#f59e0b" style={{ flexShrink: 0, marginTop: 2 }}/>
            <p style={{ fontSize: '0.67rem', color: '#78716c', lineHeight: 1.55, margin: 0 }}>
              <strong style={{ color: '#f59e0b' }}>Educational simulation only.</strong>{' '}
              Historical lines use real adjusted close prices from Alpha Vantage.
              Forward projections use sector CAGR averages — not a forecast.
              Equal-weight blended portfolio. Past performance does not guarantee future results.
              This is not investment advice.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TopWinnersAgent;